// Code generated by apic; DO NOT EDIT.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useMemo, type ChangeEvent } from "react";
import type { WsEndpointMeta } from "./types";
import type { ApicClient } from "../react/client";
import { getApiToken } from "../react/auth";
import { tryCanSend } from "./TryPanel";
import { AuthBadge } from "./AuthBadge";
import { SurfaceSamples } from "./SurfaceSamples";
import {
  wsCurlSample,
  wsGoSample,
  wsPythonSample,
  wsTsSample,
} from "./codegen/websocket";

/** Maximum number of log entries retained in the frame log. */
const MAX_LOG_ENTRIES = 200;

/** Truncate a string to MAX_PREVIEW_CHARS characters (UTF-16 code units). */
const MAX_PREVIEW_CHARS = 512;
function truncate(s: string): string {
  if (s.length <= MAX_PREVIEW_CHARS) {
    return s;
  }
  return s.slice(0, MAX_PREVIEW_CHARS) + "… (truncated)";
}

/**
 * wsIsAvailable returns true when the WebSocket constructor is available in the
 * current runtime. Checked at call time — NEVER at module load — so this
 * module is safe to import on React Native (Hermes), where WebSocket may be
 * absent or injected late.
 */
function wsIsAvailable(client: ApicClient): boolean {
  // If the caller injected a factory into the client config, assume available.
  const cfg = (client as any).config as { webSocketFactory?: unknown } | undefined;
  if (cfg && typeof cfg.webSocketFactory === "function") {
    return true;
  }
  // Fall back to the global WebSocket check.
  return typeof WebSocket !== "undefined";
}

/**
 * FrameLogEntry is one entry in the bounded scrolling frame log.
 */
type FrameLogEntry = {
  id: number;
  timestamp: string;
  direction: "in" | "out" | "info" | "error";
  content: string;
};

/**
 * WebSocketPanel renders one card per WebSocket endpoint with:
 *   - Path, protocol badge, AuthBadge, and message-schema name.
 *   - A code-sample strip (curl/websocat | Go | Python | TypeScript)
 *     via SurfaceSamples.
 *   - A Connect / Disconnect control (capability-guarded + auth-gated).
 *   - A message input + Send button (JSON for json proto; note for binary).
 *   - A bounded scrolling frame log (cap MAX_LOG_ENTRIES).
 *
 * Props:
 *   endpoints  - the list of WsEndpointMeta from ServiceMetadata.websockets.
 *   client     - a ApicClient provided by the parent via useExplorerClient.
 *   baseUrl    - optional base URL prefix (used for code samples and connect URL).
 *   className  - extra CSS class appended to the root element.
 *
 * Connect is capability-guarded: if WebSocket is unavailable in the current
 * runtime (no global WebSocket and no client.config.webSocketFactory), a clear
 * note is rendered and Connect is disabled. This is checked at click time
 * only — not at module load — so the module is safe to import on React Native.
 *
 * Auth gating mirrors TryPanel / GraphQLPanel: cert-required and
 * token-required-without-token endpoints disable Connect with an explanatory
 * note.
 *
 * No web-global access at module load time (RN-import-safe).
 */
export function WebSocketPanel(props: {
  endpoints: WsEndpointMeta[];
  client: ApicClient;
  baseUrl?: string;
  className?: string;
}): JSX.Element {
  const { endpoints, client, className, baseUrl } = props;
  const root = ["apic-ui-websocket-panel", className].filter(Boolean).join(" ");

  return (
    <div className={root}>
      {endpoints.length === 0 && (
        <div className="apic-ui-websocket-panel-empty">
          {"No WebSocket endpoints exposed."}
        </div>
      )}
      {endpoints.map((ep) => (
        <WebSocketCard
          key={ep.path + ":" + ep.proto}
          ep={ep}
          client={client}
          {...(baseUrl !== undefined ? { baseUrl } : {})}
        />
      ))}
    </div>
  );
}

/**
 * WebSocketCard renders a single WebSocket endpoint card with connect /
 * disconnect controls, message input, code samples, and a bounded scrolling frame log.
 */
function WebSocketCard(props: {
  ep: WsEndpointMeta;
  client: ApicClient;
  baseUrl?: string;
}): JSX.Element {
  const { ep, client, baseUrl } = props;

  // Connection state.
  const [connected, setConnected] = useState<boolean>(false);
  const [connecting, setConnecting] = useState<boolean>(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  // Frame log: bounded array of entries.
  const [log, setLog] = useState<FrameLogEntry[]>([]);
  // Monotonic counter — avoids Date.now() at module scope.
  const counterRef = useRef<number>(0);

  // Message input (JSON proto only).
  const [msgText, setMsgText] = useState<string>("");
  const [sendError, setSendError] = useState<string | null>(null);

  // Stream ref for cleanup.
  const streamRef = useRef<{ close(): void; send?: (v: unknown) => void } | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  // Sentinel at the end of the frame log used to auto-scroll to the newest entry.
  const logEndRef = useRef<HTMLDivElement | null>(null);

  // Re-evaluate token on every render so gating reflects the current token.
  const hasToken = getApiToken() !== null;
  const canSendResult = tryCanSend(ep.auth, hasToken);

  // Build WS code samples. useMemo so they only rebuild when inputs change.
  // NOTE: the sample context deliberately OMITS the live token so that the
  // generated code snippets use the "<token>" placeholder. This prevents the
  // real bearer token from appearing in copyable <code> DOM elements, defeating
  // TokenBar's password masking. The connect path authenticates through the
  // generated client, which reads the live token internally — so this component
  // never needs to capture the token itself. (R2-1 security fix.)
  const resolvedBase = baseUrl !== undefined && baseUrl !== "" ? baseUrl : "https://api.example.com";

  const wsSamples = useMemo(() => {
    // Sample context: baseUrl only — NO token/apiKey.
    // Builders fall back to "<token>"/"<api-key>" placeholders when absent.
    const ctx = {
      baseUrl: resolvedBase,
    };
    return {
      curl:       wsCurlSample(ep, ctx),
      go:         wsGoSample(ep, ctx),
      python:     wsPythonSample(ep, ctx),
      typescript: wsTsSample(ep, ctx),
    };
  }, [ep, resolvedBase]);

  function appendLog(direction: FrameLogEntry["direction"], content: string): void {
    counterRef.current += 1;
    const id = counterRef.current;
    // Timestamp derived INSIDE the event handler — never at module load.
    const timestamp = new Date().toISOString();
    setLog((prev) => {
      const next = [...prev, { id, timestamp, direction, content }];
      // Cap at MAX_LOG_ENTRIES, dropping oldest.
      return next.length > MAX_LOG_ENTRIES ? next.slice(next.length - MAX_LOG_ENTRIES) : next;
    });
  }

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      unsubRef.current?.();
      unsubRef.current = null;
      streamRef.current?.close();
      streamRef.current = null;
    };
  }, []);

  // Auto-scroll the frame log to the newest entry whenever it grows.
  // scrollIntoView is capability-guarded: it is absent on React Native, so the
  // optional-chain no-ops there rather than throwing.
  useEffect(() => {
    logEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [log]);

  async function handleConnect(): Promise<void> {
    // Capability guard: check at click time, not module load.
    if (!wsIsAvailable(client)) {
      setConnectError(
        "WebSocket is unavailable in this runtime. " +
        "Provide config.webSocketFactory to connect on React Native.",
      );
      return;
    }
    setConnecting(true);
    setConnectError(null);
    try {
      // Derive the connect method name: "connect" + ep.methodName
      const connectKey = "connect" + ep.methodName;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (typeof (client as any)[connectKey] !== "function") {
        throw new Error(
          "connect method '" + connectKey + "' not found on client. " +
          "The generated client may not expose this endpoint.",
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const stream = await (client as any)[connectKey]() as {
        close(): void;
        onMessage(handler: (v: unknown) => void): () => void;
        send?(v: unknown): void;
      };
      streamRef.current = stream;
      // Clear the log only once the connection is confirmed so a failed attempt
      // does not wipe entries from a previous session.
      setLog([]);
      setConnected(true);
      appendLog("info", "Connected to " + ep.path);

      const unsub = stream.onMessage((msg: unknown) => {
        let content: string;
        if (msg instanceof Uint8Array) {
          content = "[binary " + msg.byteLength + " bytes]";
        } else {
          try {
            content = truncate(JSON.stringify(msg, null, 2));
          } catch {
            content = truncate(String(msg));
          }
        }
        appendLog("in", content);
      });
      unsubRef.current = unsub;
    } catch (err: unknown) {
      // Clean up a partially-opened stream so a socket can never leak when
      // subscription (or any post-open step) throws synchronously.
      unsubRef.current?.();
      unsubRef.current = null;
      streamRef.current?.close();
      streamRef.current = null;
      setConnected(false);
      const msg = err instanceof Error ? err.message : String(err);
      setConnectError(msg);
      appendLog("error", "Connection failed: " + msg);
    } finally {
      setConnecting(false);
    }
  }

  function handleDisconnect(): void {
    unsubRef.current?.();
    unsubRef.current = null;
    streamRef.current?.close();
    streamRef.current = null;
    setConnected(false);
    appendLog("info", "Disconnected from " + ep.path);
  }

  function handleSend(): void {
    setSendError(null);
    if (streamRef.current === null) {
      setSendError("Not connected.");
      return;
    }
    if (ep.proto === "binary") {
      setSendError("Binary send is not supported from the browser explorer.");
      return;
    }
    const text = msgText.trim();
    if (text === "") {
      setSendError("Message is empty.");
      return;
    }
    let payload: unknown;
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      setSendError("Invalid JSON message.");
      return;
    }
    try {
      if (typeof streamRef.current.send === "function") {
        streamRef.current.send(payload);
        appendLog("out", truncate(JSON.stringify(payload, null, 2)));
        setMsgText("");
      } else {
        setSendError("Send is not available on this connection.");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSendError("Send failed: " + msg);
    }
  }

  const isBinary = ep.proto === "binary";
  const isConnectDisabled = !canSendResult.enabled || connecting || connected;

  return (
    <div className="apic-ui-websocket-card">
      {/* Header: path + protocol badge */}
      <div className="apic-ui-websocket-card-header">
        <span className="apic-ui-websocket-card-path">{ep.path}</span>
        <span className={"apic-ui-chip apic-ui-chip--proto apic-ui-chip--proto-" + ep.proto}>
          {ep.proto}
        </span>
      </div>

      {/* Auth badge */}
      <div className="apic-ui-websocket-card-auth">
        <AuthBadge auth={ep.auth} className="apic-ui-websocket-card-auth-badge" />
      </div>

      {/* Message schema name */}
      {ep.messageSchema !== null && (
        <div className="apic-ui-websocket-card-schema">
          {"Message schema: "}
          <code>{ep.messageSchema}</code>
        </div>
      )}

      {/* Code samples — curl/websocat | Go | Python | TypeScript */}
      <SurfaceSamples
        samples={wsSamples}
        className="apic-ui-websocket-card-samples"
      />

      {/* Connect / Disconnect controls */}
      <div className="apic-ui-websocket-card-controls">
        {!connected ? (
          <button
            className={
              "apic-ui-websocket-card-connect" +
              (isConnectDisabled ? " apic-ui-websocket-card-connect--disabled" : "") +
              (connecting ? " apic-ui-websocket-card-connect--connecting" : "")
            }
            disabled={isConnectDisabled}
            onClick={() => { void handleConnect(); }}
          >
            {connecting ? "Connecting…" : "Connect"}
          </button>
        ) : (
          <button
            className="apic-ui-websocket-card-disconnect"
            onClick={handleDisconnect}
          >
            {"Disconnect"}
          </button>
        )}
        {/* Auth gate note */}
        {!canSendResult.enabled && canSendResult.note !== undefined && (
          <span className="apic-ui-websocket-card-gate-note">{canSendResult.note}</span>
        )}
        {/* Connect error note */}
        {connectError !== null && (
          <span className="apic-ui-websocket-card-connect-error">{connectError}</span>
        )}
        {/* Connection status badge */}
        <span
          className={
            "apic-ui-chip apic-ui-chip--status" +
            (connected ? " apic-ui-chip--status-connected" : " apic-ui-chip--status-disconnected")
          }
        >
          {connected ? "connected" : "disconnected"}
        </span>
      </div>

      {/* Message input — only when connected */}
      {connected && (
        <div className="apic-ui-websocket-card-send">
          {isBinary ? (
            <span className="apic-ui-websocket-card-binary-note">
              {"Binary frames cannot be sent from the browser explorer."}
            </span>
          ) : (
            <>
              <label
                className="apic-ui-websocket-card-send-label"
                htmlFor={"apic-ui-ws-msg-" + ep.path.replace(/[^a-zA-Z0-9]/g, "-")}
              >
                {"Message (JSON)"}
              </label>
              <textarea
                id={"apic-ui-ws-msg-" + ep.path.replace(/[^a-zA-Z0-9]/g, "-")}
                className="apic-ui-websocket-card-send-textarea"
                rows={3}
                placeholder={"{}"}
                value={msgText}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => { setMsgText(e.target.value); }}
              />
              <button
                className="apic-ui-websocket-card-send-btn"
                onClick={handleSend}
              >
                {"Send"}
              </button>
              {sendError !== null && (
                <span className="apic-ui-websocket-card-send-error">{sendError}</span>
              )}
            </>
          )}
        </div>
      )}

      {/* Frame log */}
      {log.length > 0 && (
        <div className="apic-ui-websocket-card-log">
          <div className="apic-ui-websocket-card-log-header">
            {"Frame log (" + log.length + " / " + MAX_LOG_ENTRIES + ")"}
          </div>
          <div className="apic-ui-websocket-card-log-entries">
            {log.map((entry) => (
              <div
                key={entry.id}
                className={"apic-ui-websocket-card-log-entry apic-ui-websocket-card-log-entry--" + entry.direction}
              >
                <span className="apic-ui-websocket-card-log-ts">{entry.timestamp}</span>
                <span className="apic-ui-websocket-card-log-dir">{entry.direction}</span>
                <pre className="apic-ui-websocket-card-log-content">
                  <code>{entry.content}</code>
                </pre>
              </div>
            ))}
            {/* Scroll sentinel — auto-scrolled into view as the log grows. */}
            <div ref={logEndRef} className="apic-ui-websocket-card-log-end" />
          </div>
        </div>
      )}
    </div>
  );
}
